import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FullApplicationSkeleton } from './SkeletonComponents.module';
import styles from './skeleton.module.css';

interface FullAppSkeletonProps {
  isLoading: boolean;
}

// Route-based skeleton type detection (same logic as SmartSkeleton)
const getSkeletonType = (pathname: string): 'dashboard' | 'table' | 'form' | 'charts' | 'default' => {
  // Dashboard routes
  if (
      pathname.includes('/projectManagement/dashboard') ||
      pathname.includes('/designManagement/dashboard')) 
  {
    return 'dashboard';
  }
  
  // Charts routes
  if (pathname.includes('/financeManagement/dashboard')) {
    return 'charts';
  }
  
  // Table/listing routes
  if (
      pathname === '/' ||
      (pathname.includes('/users') && !pathname.includes('/update') && !pathname.includes('/create')) ||
      (pathname.includes('/subcontractors') && !pathname.includes('/details')) ||
      (pathname.includes('/offices') && !pathname.includes('/update') && !pathname.includes('/create')) ||
      pathname.includes('/overview-template') ||
      pathname.includes('/notifications') ||
      pathname.includes('/folders') ||
      pathname.includes('/listing') || 
      
      (pathname.includes('/sales/projects') && !pathname.includes('/details')) ||
      (pathname.includes('/sales/proposals') && !pathname.includes('/details')) ||
      (pathname.includes('/financeManagement/projects') && !pathname.includes('/details'))
    ) {
    return 'table';
  }
  
  // Form routes (create/edit)
  if (pathname.includes('/create') || 
      pathname.includes('/edit') ||
      pathname.includes('/update') ||
      pathname.includes('/add')) {
    return 'form';
  }
  
  // Detail pages and others
  if (pathname.includes('/details')) {
    return 'default';
  }
  
  return 'default';
};

export const FullAppSkeleton: React.FC<FullAppSkeletonProps> = ({ isLoading }) => {
  const location = useLocation();
  const [showSkeleton, setShowSkeleton] = useState(isLoading);
  const [fadeClass, setFadeClass] = useState('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const fadeTimeoutRef = useRef<NodeJS.Timeout>();
  const previousLoadingRef = useRef(isLoading);

  const skeletonType = getSkeletonType(location.pathname);

  // Handle smooth transitions with debounce logic
  useEffect(() => {
    const wasLoading = previousLoadingRef.current;
    previousLoadingRef.current = isLoading;

    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = undefined;
    }

    if (isLoading && !wasLoading) {
      // Starting to load - show skeleton immediately
      console.log('🚀 FullAppSkeleton: Loading started, showing skeleton immediately');
      setShowSkeleton(true);
      setFadeClass('fadeIn');

      // Clear any pending fade out
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = undefined;
      }
    } else if (!isLoading && wasLoading) {
      // Finished loading - but wait to see if it goes back to loading
      // This prevents glitchy flashes during quick loading state changes
      console.log('🕐 FullAppSkeleton: Starting 500ms debounce wait...');

      debounceTimeoutRef.current = setTimeout(() => {
        // After debounce period, check if still not loading
        if (!isLoading) {
          console.log('✅ FullAppSkeleton: Debounce complete, starting fade out');
          // Start fade out
          setFadeClass('fadeOut');

          // Hide skeleton after fade completes
          fadeTimeoutRef.current = setTimeout(() => {
            console.log('🫥 FullAppSkeleton: Fade complete, hiding skeleton');
            setShowSkeleton(false);
            setFadeClass('');
          }, 400); // Match CSS transition duration
        } else {
          console.log('🔄 FullAppSkeleton: Loading resumed during debounce, keeping skeleton');
        }
      }, 500); // 0.5 second debounce as requested
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [isLoading]);

  // Handle route changes - show skeleton immediately for new routes
  useEffect(() => {
    if (isLoading) {
      // Clear any pending debounce when route changes and loading starts
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = undefined;
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = undefined;
      }

      setShowSkeleton(true);
      setFadeClass('fadeIn');
    }
  }, [location.pathname, isLoading]);

  if (!showSkeleton) {
    return null;
  }

  return (
    <div className={`${styles.fullSkeletonOverlay} ${fadeClass} full-app-skeleton-overlay`}>
      <FullApplicationSkeleton type={skeletonType} />
    </div>
  );
};

export default FullAppSkeleton;
